import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { buildApiUrl } from '../../config/api';
import './MeusCertificados.css';

const CertificateGlyph = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="4" y="3" width="13" height="16" rx="2" stroke="currentColor" strokeWidth="1.8"/>
    <path d="M8 8h5M8 12h4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
    <circle cx="17" cy="16" r="3.5" fill="currentColor" stroke="currentColor" strokeWidth="1.2"/>
    <path d="m15.5 19 1.5 2 1.5-2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const CertificateSectionHeading = ({ variant, title, description, count }) => (
  <div className={`certificate-section-heading certificate-section-heading--${variant}`}>
    <span className="certificate-section-icon"><CertificateGlyph /></span>
    <div className="certificate-section-copy">
      <h2>{title}</h2>
      <p>{description}</p>
    </div>
    <span className="certificate-section-count">{count}</span>
  </div>
);

const uniqueResultsByCourse = (results = []) => Array.from(
  new Map(results.map(result => [result.curso?.id ?? result.id, result])).values()
);

const MeusCertificados = () => {
  const [certificados, setCertificados] = useState([]);
  const [cursosDisponiveis, setCursosDisponiveis] = useState([]);
  const [cursosPendentes, setCursosPendentes] = useState([]);
  const [downloadingId, setDownloadingId] = useState(null);

  // CPF/RG do usuário
  const [userCpf, setUserCpf] = useState('');
  const [userRg, setUserRg] = useState('');
  const [userData, setUserData] = useState(null);

  // Modal para coletar CPF/RG quando ausentes
  const [showModal, setShowModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null); // { type: 'download'|'visualizar', cursoId, cursoTitle }
  const [modalCpf, setModalCpf] = useState('');
  const [modalRg, setModalRg] = useState('');
  const [modalErrors, setModalErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // Máscaras
  const applyCpfMask = (v) => {
    const n = v.replace(/\D/g, '').slice(0, 11);
    return n.replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d{1,2})$/, '$1-$2');
  };
  const applyRgMask = (v) => v.replace(/[^0-9.-]/g, '').slice(0, 20);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    // Busca certificados
    axios.get(buildApiUrl('meus-certificados'), { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {

        setCertificados(res.data.certificados || []);
        setCursosDisponiveis(uniqueResultsByCourse(res.data.cursosDisponiveis));
        setCursosPendentes(uniqueResultsByCourse(res.data.cursosPendentes));
      })
      .catch(() => {})

    // Busca dados do usuário (cpf/rg)
    axios.get(buildApiUrl('user'), { headers: { Authorization: `Bearer ${token}` } })
      .then(res => { setUserData(res.data); setUserCpf(res.data.cpf || ''); setUserRg(res.data.rg || ''); })
      .catch(() => {})
  }, []);

  // Verifica antes de agir — se não tem cpf/rg, abre modal
  const handleCertificateAction = (type, cursoId, cursoTitle) => {
    if (!userCpf || !userRg) {
      setPendingAction({ type, cursoId, cursoTitle });
      setModalCpf(userCpf || '');
      setModalRg(userRg || '');
      setModalErrors({});
      setShowModal(true);
    } else {
      executarAction(type, cursoId, cursoTitle);
    }
  };

  // Salva CPF/RG e executa a ação pendente
  const handleModalSubmit = async () => {
    const errors = {};
    if (!/^\d{3}\.\d{3}\.\d{3}-\d{2}$/.test(modalCpf)) errors.cpf = 'CPF inválido (000.000.000-00)';
    if (!modalRg || modalRg.length < 5) errors.rg = 'RG inválido';
    if (Object.keys(errors).length) { setModalErrors(errors); return; }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const payload = { ...userData, cpf: modalCpf, rg: modalRg };
      await axios.post(buildApiUrl('user'), payload, { headers: { Authorization: `Bearer ${token}` } });
      setUserCpf(modalCpf);
      setUserRg(modalRg);
      setShowModal(false);
      if (pendingAction) executarAction(pendingAction.type, pendingAction.cursoId, pendingAction.cursoTitle);
    } catch (err) {
      setModalErrors({ geral: 'Erro ao salvar. Tente novamente.' });
    } finally {
      setSaving(false);
    }
  };

  const executarAction = async (type, cursoId, cursoTitle) => {
    const token = localStorage.getItem('token');
    if (type === 'download') setDownloadingId(cursoId);
    try {
      const response = await axios.get(buildApiUrl(`certificados/${cursoId}/download`), {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      if (type === 'download') {
        const link = document.createElement('a');
        link.href = url;
        link.download = `Certificado_${cursoTitle.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        alert(`Certificado de "${cursoTitle}" baixado com sucesso!`);
      } else {
        window.open(url, '_blank');
        setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      }
    } catch (error) {

      alert('Erro ao processar o certificado. Tente novamente.');
    } finally {
      if (type === 'download') setDownloadingId(null);
    }
  };

  return (
    <>
    <div className="meus-certificados-page">
      {/* Hero Header */}
      <div className="meus-certificados-hero">
        <div className="hero-content">
          <h1>Meus Certificados</h1>
          <p className="hero-subtitle">Acompanhe e baixe seus certificados de conclusão</p>
        </div>
      </div>

      <div className="meus-certificados-container">{certificados.length === 0 && cursosDisponiveis.length === 0 && cursosPendentes.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">🏆</div>
          <h2 className="empty-title">Nenhum certificado encontrado</h2>
          <p className="empty-message">
            Você ainda não possui certificados. Complete seus cursos e obtenha
            certificados de conclusão para comprovar seu aprendizado.
          </p>
          <Link to="/cursos" className="empty-cta">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Explorar Cursos
          </Link>
        </div>
      ) : (
        <div className="certificates-content">
          {cursosPendentes.length > 0 && (
            <div className="pending-certificates-section">
              <CertificateSectionHeading
                variant="pending"
                title="Pagamentos em processamento"
                description="Conclua o pagamento para liberar e baixar seus certificados."
                count={cursosPendentes.length}
              />
              
              <div className="pending-certificates-grid">
                {cursosPendentes.map(cursoResult => (
                  <article key={cursoResult.curso.id} className="certificado-card certificate-list-card pending">
                    <div className="certificate-card-main">
                      <div className="certificate-icon certificate-icon--pending">
                        <CertificateGlyph />
                      </div>
                      <div className="certificate-copy">
                        <h3 className="certificate-title">{cursoResult.curso.title}</h3>
                        <p className="pending-message">Comprove sua qualificação, valorize seu currículo e esteja pronto para novas oportunidades.</p>
                      </div>
                    </div>
                    <div className="certificate-card-side">
                      <span className="status-badge pending">AGUARDANDO PAGAMENTO</span>
                      <div className="certificate-actions">
                      <Link 
                        to={`/cart/add?curso_id=${cursoResult.curso.id}`} 
                        className="action-button primary full-width"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                          <path d="M12 6V12L16 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                        Emitir Certificado
                      </Link>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          )}
          {certificados.length > 0 && (
            <div className="certificates-section">
              <CertificateSectionHeading
                variant="released"
                title="Certificados liberados"
                description="Visualize ou baixe seus certificados sempre que precisar."
                count={certificados.length}
              />
              
              <div className="certificados-grid">
                {certificados.map(certificado => (
                  <article key={certificado.id} className="certificado-card certificate-list-card released">
                    <div className="certificate-card-main">
                      <div className="certificate-icon certificate-icon--released">
                        <CertificateGlyph />
                      </div>
                      <div className="certificate-copy">
                        <h3 className="certificate-title">{certificado.title}</h3>
                        <div className="certificate-meta">
                          <div className="meta-item">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M9 11H15M9 15H15M17 21L21 17L17 13M7 21H4C3.45 21 3 20.55 3 20V4C3 3.45 3.45 3 4 3H20C20.55 3 21 3.45 21 4V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            <span><strong>Nota:</strong> {certificado.user_results[0]?.score || 0}/100</span>
                          </div>
                          <div className="meta-item">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
                              <line x1="16" y1="2" x2="16" y2="6" stroke="currentColor" strokeWidth="2"/>
                              <line x1="8" y1="2" x2="8" y2="6" stroke="currentColor" strokeWidth="2"/>
                              <line x1="3" y1="10" x2="21" y2="10" stroke="currentColor" strokeWidth="2"/>
                            </svg>
                            <span><strong>Concluído em:</strong> {new Date(certificado.user_results[0]?.created_at || Date.now()).toLocaleDateString('pt-BR')}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="certificate-card-side">
                      <span className="status-badge paid">LIBERADO</span>
                      <div className="certificate-actions certificate-actions--double">
                      <button
                        onClick={() => handleCertificateAction('visualizar', certificado.id, certificado.title)}
                        className="action-button secondary"
                        title="Visualizar certificado no navegador"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M1 12S5 4 12 4S23 12 23 12S19 20 12 20S1 12 1 12Z" stroke="currentColor" strokeWidth="2"/>
                          <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2"/>
                        </svg>
                        Visualizar
                      </button>
                      
                      <button
                        onClick={() => handleCertificateAction('download', certificado.id, certificado.title)}
                        disabled={downloadingId === certificado.id}
                        className="action-button primary"
                        title="Baixar certificado em PDF"
                      >
                        {downloadingId === certificado.id ? (
                          <>
                            <div className="button-spinner"></div>
                            Baixando...
                          </>
                        ) : (
                          <>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              <polyline points="7,10 12,15 17,10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              <line x1="12" y1="15" x2="12" y2="3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            Baixar PDF
                          </>
                        )}
                      </button>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          )}
          {cursosDisponiveis.length > 0 && (
            <div className="available-certificates-section">
              <CertificateSectionHeading
                variant="available"
                title="Certificado disponível para pagamento"
                description="Realize o pagamento para liberar seu certificado e comprovar sua qualificação."
                count={cursosDisponiveis.length}
              />
              
              <div className="available-certificates-grid">
                {cursosDisponiveis.map(cursoResult => (
                  <article key={cursoResult.curso.id} className="certificado-card certificate-list-card available">
                    <div className="certificate-card-main">
                      <div className="certificate-icon certificate-icon--available">
                        <CertificateGlyph />
                      </div>
                      <div className="certificate-copy">
                        <h3 className="certificate-title">{cursoResult.curso.title}</h3>
                        <div className="certificate-meta">
                          <div className="meta-item">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M9 11H15M9 15H15M17 21L21 17L17 13M7 21H4C3.45 21 3 20.55 3 20V4C3 3.45 3.45 3 4 3H20C20.55 3 21 3.45 21 4V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            <span><strong>Nota:</strong> {cursoResult.score || 0}/100</span>
                          </div>
                          <div className="meta-item">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
                              <line x1="16" y1="2" x2="16" y2="6" stroke="currentColor" strokeWidth="2"/>
                              <line x1="8" y1="2" x2="8" y2="6" stroke="currentColor" strokeWidth="2"/>
                              <line x1="3" y1="10" x2="21" y2="10" stroke="currentColor" strokeWidth="2"/>
                            </svg>
                            <span><strong>Aprovado em:</strong> {new Date(cursoResult.created_at || Date.now()).toLocaleDateString('pt-BR')}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="certificate-card-side">
                      <span className="status-badge pending">AGUARDANDO PAGAMENTO</span>
                      <div className="certificate-actions">
                      <Link 
                        to={`/cart/add?curso_id=${cursoResult.curso.id}`} 
                        className="action-button primary full-width"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M4 7V4C4 3.44772 4.44772 3 5 3H19C19.5523 3 20 3.44772 20 4V7" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                          <rect x="3" y="7" width="18" height="13" rx="2" stroke="currentColor" strokeWidth="2"/>
                          <path d="M12 11L9 14L15 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        Emitir Certificado
                      </Link>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      </div>
    </div>

    {/* Modal CPF/RG */}
    {showModal && (
      <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.55)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:9999 }}>
        <div style={{ background:'#fff', borderRadius:'12px', padding:'2rem', width:'100%', maxWidth:'420px', boxShadow:'0 20px 60px rgba(0,0,0,0.3)' }}>
          <h2 style={{ margin:'0 0 0.5rem', fontSize:'1.3rem', color:'#1f2d3d' }}>Dados necessários para o certificado</h2>
          <p style={{ margin:'0 0 1.5rem', fontSize:'0.9rem', color:'#7f8c8d' }}>
            Para emitir seu certificado, precisamos do seu CPF e RG. Esses dados serão salvos na sua conta e não poderão ser alterados futuramente.
          </p>

          <div style={{ marginBottom:'1rem' }}>
            <label style={{ display:'block', fontSize:'0.85rem', fontWeight:600, color:'#2c3e50', marginBottom:'0.35rem' }}>CPF *</label>
            <input
              type="text"
              value={modalCpf}
              onChange={e => setModalCpf(applyCpfMask(e.target.value))}
              placeholder="000.000.000-00"
              maxLength={14}
              style={{ width:'100%', padding:'0.7rem 1rem', border:`1.5px solid ${modalErrors.cpf ? '#e74c3c' : '#dde3ea'}`, borderRadius:'8px', fontSize:'1rem', boxSizing:'border-box', outline:'none' }}
            />
            {modalErrors.cpf && <span style={{ fontSize:'0.8rem', color:'#e74c3c' }}>{modalErrors.cpf}</span>}
          </div>

          <div style={{ marginBottom:'1.5rem' }}>
            <label style={{ display:'block', fontSize:'0.85rem', fontWeight:600, color:'#2c3e50', marginBottom:'0.35rem' }}>RG *</label>
            <input
              type="text"
              value={modalRg}
              onChange={e => setModalRg(applyRgMask(e.target.value))}
              placeholder="Ex: 12.345.678-9"
              maxLength={20}
              style={{ width:'100%', padding:'0.7rem 1rem', border:`1.5px solid ${modalErrors.rg ? '#e74c3c' : '#dde3ea'}`, borderRadius:'8px', fontSize:'1rem', boxSizing:'border-box', outline:'none' }}
            />
            {modalErrors.rg && <span style={{ fontSize:'0.8rem', color:'#e74c3c' }}>{modalErrors.rg}</span>}
          </div>

          {modalErrors.geral && <p style={{ color:'#e74c3c', fontSize:'0.85rem', marginBottom:'1rem' }}>{modalErrors.geral}</p>}

          <div style={{ display:'flex', gap:'0.75rem' }}>
            <button
              onClick={() => setShowModal(false)}
              style={{ flex:1, padding:'0.75rem', background:'#f0f0f0', border:'none', borderRadius:'8px', cursor:'pointer', fontWeight:600, color:'#555' }}
            >
              Cancelar
            </button>
            <button
              onClick={handleModalSubmit}
              disabled={saving}
              style={{ flex:2, padding:'0.75rem', background:'linear-gradient(135deg,#7c3aed,#9f1ed6)', border:'none', borderRadius:'8px', cursor:'pointer', fontWeight:700, color:'#fff', fontSize:'1rem' }}
            >
              {saving ? 'Salvando...' : 'Salvar e emitir certificado'}
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default MeusCertificados;
